import { z } from 'zod';
import '@apexdesigner/schema-tools';
import {
  belongsTo,
  hasMany,
  options,
  references,
} from '@apexdesigner/schema-tools';

export const processInstance = z
  .object({
    id: z.uuid().hidden().describe('Unique identifier'),

    referenceNumber: z
      .number()
      .nullable()
      .optional()
      .describe('Human-readable reference number'),

    name: z
      .string()
      .nullable()
      .optional()
      .describe('Optional name to identify this instance'),

    status: options([
      { value: 'Active', name: 'Active' },
      { value: 'Paused', name: 'Paused' },
      { value: 'Canceled', name: 'Canceled' },
      { value: 'Complete', name: 'Complete' },
      { value: 'Error', name: 'Error' },
    ])
      .nullable()
      .optional()
      .describe('Current state of this instance'),

    data: z
      .unknown()
      .nullable()
      .optional()
      .presentAs('json')
      .describe('Process data and variable values'),

    errorMessage: z
      .string()
      .nullable()
      .optional()
      .presentAs('textarea')
      .describe('Error details if the instance failed'),

    errorCode: z
      .string()
      .nullable()
      .optional()
      .describe('Stable error code if the instance failed with a known error'),

    claimedBy: z
      .string()
      .nullable()
      .optional()
      .hidden()
      .describe('Server node processing this instance'),

    claimedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('When processing started on this instance'),

    completedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .describe('When this instance finished'),

    loopIndex: z
      .number()
      .nullable()
      .optional()
      .describe('Loop index for multi-instance Call Activity'),

    resultApplied: z
      .boolean()
      .nullable()
      .optional()
      .hidden()
      .describe(
        'For a Call Activity child: whether its completion has been folded into the parent (before-complete hook and output mappings)',
      ),

    candidateUserId: z
      .string()
      .nullable()
      .optional()
      .describe(
        'Candidate user id, generated from the data.candidateUserId JSON key',
      ),

    createdAt: z.coerce
      .date()
      .nullable()
      .optional()
      .hidden()
      .describe('createdAt'),

    updatedAt: z.coerce
      .date()
      .nullable()
      .optional()
      .hidden()
      .describe('updatedAt'),

    processDesignId: z.uuid().hidden().describe('Foreign key to ProcessDesign'),

    parentTokenId: z
      .uuid()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to Token'),

    tenantId: z
      .uuid()
      .nullable()
      .optional()
      .presentAs('reference')
      .describe('Foreign key to Tenant'),

    processDesign: belongsTo('ProcessDesign'),

    parentToken: references('Token'),

    processInstanceHistories: hasMany('ProcessInstanceHistory'),

    pendingStateUpdates: hasMany('PendingStateUpdate'),

    tokens: hasMany('Token'),

    tenant: references('Tenant'),
  })
  .describe('A running instance of a process design')
  .as('ProcessInstance');

export { processInstance as processInstanceSchema };
